import { createTheme, alpha } from '@mui/material/styles';

// Fleet operations palette: graphite surfaces, electric teal actions, warm amber alerts.
const colors = {
  // Primary gradient colors
  primary: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    gradient: '#3b82f6',
  },
  // Secondary accent
  secondary: {
    main: '#f59e0b', // Amber
    light: '#fbbf24',
    dark: '#d97706',
  },
  // Dark mode background colors
  background: {
    default: '#0b1320',
    paper: '#111d2d',
    card: 'rgba(17, 29, 45, 0.84)',
    elevated: '#17283a',
    gradient: 'linear-gradient(180deg, #0b1320 0%, #111d2d 100%)',
  },
  // Text colors
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.5)',
  },
  // Status colors
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#38bdf8',
  // Glassmorphism
  glass: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: 'rgba(255, 255, 255, 0.1)',
    hover: 'rgba(255, 255, 255, 0.08)',
  },
};

// Glassmorphism effect helper
export const glassEffect = {
  background: colors.glass.background,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: `1px solid ${colors.glass.border}`,
  borderRadius: '16px',
};

// Gradient text helper
export const gradientText = {
  background: colors.primary.gradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    success: { main: colors.success },
    warning: { main: colors.warning },
    error: { main: colors.error },
    info: { main: colors.info },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontSize: '2.75rem',
      fontWeight: 800,
      letterSpacing: '0',
      lineHeight: 1.1,
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 700,
      letterSpacing: '0',
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 700,
      letterSpacing: '0',
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '0',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0, 0, 0, 0.15)',
    '0 4px 12px rgba(0, 0, 0, 0.15)',
    '0 6px 16px rgba(0, 0, 0, 0.2)',
    '0 8px 24px rgba(0, 0, 0, 0.2)',
    '0 12px 32px rgba(0, 0, 0, 0.25)',
    '0 16px 40px rgba(0, 0, 0, 0.3)',
    '0 20px 48px rgba(0, 0, 0, 0.3)',
    '0 24px 56px rgba(0, 0, 0, 0.35)',
    '0 28px 64px rgba(0, 0, 0, 0.35)',
    '0 32px 72px rgba(0, 0, 0, 0.4)',
    ...Array(14).fill('0 32px 72px rgba(0, 0, 0, 0.4)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          backgroundColor: colors.background.default,
          backgroundImage: `
            radial-gradient(ellipse at 12% 8%, rgba(59, 130, 246, 0.10) 0%, transparent 42%),
            ${colors.background.gradient}
          `,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        '::selection': {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
        },
        // MUI's default focus overlay is invisible on graphite; give keyboard users a real ring.
        // `transition: none` because several components use `transition: all`, which would
        // animate the ring in over 300ms and make it feel laggy.
        '.Mui-focusVisible, :focus-visible': {
          outline: `2px solid ${colors.primary.light} !important`,
          outlineOffset: '2px',
          transition: 'none !important',
        },
        // Filled buttons need contrast against their own blue fill
        '.MuiButton-contained.Mui-focusVisible': {
          outlineColor: '#ffffff !important',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.02)',
        },
        '::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '10px 24px',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(59, 130, 246, 0.35)',
          },
        },
        containedPrimary: {
          background: colors.primary.gradient,
          '&:hover': {
            background: '#3b82f6',
          },
        },
        outlined: {
          borderColor: colors.glass.border,
          backdropFilter: 'blur(10px)',
          '&:hover': {
            borderColor: colors.primary.light,
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          ...glassEffect,
          backgroundColor: colors.background.card,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
            borderColor: colors.primary.main,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
          overflow: 'visible',
        },
        elevation1: {
          ...glassEffect,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        },
        elevation2: {
          ...glassEffect,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
        },
        elevation3: {
          ...glassEffect,
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          ...glassEffect,
          backgroundColor: alpha(colors.background.default, 0.8),
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: alpha(colors.background.paper, 0.95),
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${colors.glass.border}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(colors.background.elevated, 0.5),
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: colors.glass.border,
            },
            '&:hover fieldset': {
              borderColor: alpha(colors.primary.main, 0.5),
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.15)}`,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          backdropFilter: 'blur(10px)',
        },
        filled: {
          backgroundColor: alpha(colors.primary.main, 0.15),
          color: colors.primary.light,
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.25),
          },
        },
        outlined: {
          borderColor: colors.glass.border,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          ...glassEffect,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '0 4px',
          minHeight: 40,
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary.main, 0.15),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.glass.border,
          padding: '16px',
        },
        head: {
          backgroundColor: alpha(colors.background.elevated, 0.5),
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          color: colors.text.secondary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.05),
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          ...glassEffect,
          backgroundColor: alpha(colors.background.paper, 0.95),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(colors.background.elevated, 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${colors.glass.border}`,
          borderRadius: '8px',
          fontSize: '0.8rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
        },
        standardSuccess: {
          backgroundColor: alpha(colors.success, 0.15),
          color: colors.success,
        },
        standardWarning: {
          backgroundColor: alpha(colors.warning, 0.15),
          color: colors.warning,
        },
        standardError: {
          backgroundColor: alpha(colors.error, 0.15),
          color: colors.error,
        },
        standardInfo: {
          backgroundColor: alpha(colors.info, 0.15),
          color: colors.info,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          ...glassEffect,
          backgroundColor: alpha(colors.background.paper, 0.95),
          marginTop: '8px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary.main, 0.15),
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.2),
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        track: {
          borderRadius: 22,
          backgroundColor: alpha(colors.text.muted, 0.3),
        },
        thumb: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
              opacity: 0.5,
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(colors.primary.main, 0.1),
        },
        bar: {
          borderRadius: 4,
          background: colors.primary.gradient,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: colors.primary.gradient,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.35)',
          '&:hover': {
            boxShadow: '0 12px 32px rgba(59, 130, 246, 0.45)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.1),
            transform: 'scale(1.05)',
          },
        },
      },
    },
  },
});

// Export color palette for use in components
export { colors };
export default theme;
